// components/ChooseLookModal.tsx
import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  IconButton,
  Box,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopIcon from "@mui/icons-material/Stop";
import CheckMark from "../CheckMark";
import { AvatarGroup, CustomPhotoAvatar } from "../../../../context/types";
import { THEME_COLORS } from "../../../../context/constants";
import { checkPhotoAvatarStatus, syncCustomPhotoAvatarV3Look, syncMotionPreviewUrl } from "../../../../client/api";
import LookThreeDotMenu from "../LookThreeDotMenu";

// Component for individual look card with video hover support
interface LookCardProps {
  look: CustomPhotoAvatar;
  isSelected: boolean;
  isCurrentlySelected: boolean;
  canSelect: boolean;
  isPolling: boolean;
  isPollingMotionPreview?: boolean; // Polling for motion preview URL
  isPollingSoundEffect?: boolean; // Polling for sound effect completion
  onSelectAvatarLabActor: (look: CustomPhotoAvatar) => void;
  onOpenAddMotion?: (look: CustomPhotoAvatar) => void;
  onAddSoundEffect?: (look: CustomPhotoAvatar) => void;
  onDeleteLook?: (lookId: string) => void;
  isAddingMotion?: boolean;
  isAddingSoundEffect?: boolean;
  getLookStatusColor: (status: string) => string;
  getLookImage: (look: CustomPhotoAvatar) => string;
}

const LookCard: React.FC<LookCardProps> = ({
  look,
  isSelected,
  isCurrentlySelected,
  canSelect,
  isPolling,
  isPollingMotionPreview = false,
  isPollingSoundEffect = false,
  onSelectAvatarLabActor,
  onOpenAddMotion,
  onAddSoundEffect,
  onDeleteLook,
  isAddingMotion,
  isAddingSoundEffect,
  getLookStatusColor,
  getLookImage,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isPlayingSoundEffect, setIsPlayingSoundEffect] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);

  const hasMotion = look.is_motion;
  const motionPreviewUrl = look.motion_preview_url;
  // Use sound_effect_preview_url first, fallback to mp3 from file_meta
  const soundEffectPreviewUrl =
    look.background_sound_effect?.sound_effect_preview_url ||
    look.background_sound_effect?.file_meta?.meta?.audios?.mp3;

  // Handle video playback on hover
  useEffect(() => {
    if (videoRef.current && hasMotion && motionPreviewUrl) {
      if (isHovered) {
        videoRef.current.play().catch((error) => {
          console.error("Motion preview play failed:", error);
        });
      } else {
        videoRef.current.pause();
        videoRef.current.currentTime = 0;
      }
    }
  }, [isHovered, hasMotion, motionPreviewUrl]);

  // Handle sound effect playback
  const handleSoundEffectToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    if (!audioRef.current || !soundEffectPreviewUrl) return;

    if (isPlayingSoundEffect) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlayingSoundEffect(false);
    } else {
      audioRef.current.play().catch((error) => {
        console.error("Sound effect play failed:", error);
      });
      setIsPlayingSoundEffect(true);
    }
  };

  // Reset playing state when audio ends
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleEnded = () => setIsPlayingSoundEffect(false);
    audio.addEventListener("ended", handleEnded);

    return () => audio.removeEventListener("ended", handleEnded);
  }, []);

  // Build tooltip content for motion looks
  const getTooltipContent = () => {
    if (!hasMotion) return "";
    const motionType = look.motion_type || "Motion";
    const prompt = look.motion_prompt;
    if (prompt) {
      return `${motionType}: ${prompt}`;
    }
    return motionType;
  };

  const cardContent = (
    <Box
      sx={{
        position: "relative",
        borderRadius: "10px",
        overflow: "hidden",
        cursor: canSelect ? "pointer" : "not-allowed",
        border: isSelected
          ? `3px solid ${THEME_COLORS.yellow}`
          : "2px solid #ddd",
        opacity: canSelect ? 1 : 0.6,
        transition: "all 0.2s ease-in-out",
        "&:hover": canSelect
          ? {
              border: `3px solid ${THEME_COLORS.yellow}`,
              transform: "scale(1.05)",
            }
          : {},
      }}
      onClick={() => onSelectAvatarLabActor(look)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Three Dot Menu */}
      <Box
        sx={{
          position: "absolute",
          top: 8,
          right: 8,
          zIndex: 10,
        }}
      >
        <LookThreeDotMenu
          look={look}
          onOpenAddMotion={onOpenAddMotion}
          onAddSoundEffect={onAddSoundEffect}
          onDeleteLook={onDeleteLook}
          isAddingMotion={isAddingMotion}
          isAddingSoundEffect={isAddingSoundEffect}
        />
      </Box>

      {/* Status Badge */}
      <Box
        sx={{
          position: "absolute",
          top: 8,
          right: onOpenAddMotion || onAddSoundEffect ? 40 : 8,
          zIndex: 5,
          padding: "2px 6px",
          borderRadius: "8px",
          fontSize: "10px",
          fontWeight: "bold",
          backgroundColor: getLookStatusColor(look.status),
          color: "white",
        }}
      >
        {look.status.charAt(0).toUpperCase() + look.status.slice(1)}
        {isPolling && "..."}
      </Box>

      {/* Motion Badge */}
      {look.is_motion && (
        <Box
          sx={{
            position: "absolute",
            bottom: 50,
            right: 8,
            zIndex: 5,
            padding: "2px 6px",
            borderRadius: "8px",
            fontSize: "9px",
            fontWeight: "bold",
            backgroundColor: isPollingMotionPreview ? "#FFA500" : "#9C27B0",
            color: "white",
            display: "flex",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          {isPollingMotionPreview && (
            <CircularProgress size={8} sx={{ color: "white" }} />
          )}
          {isPollingMotionPreview ? "Processing Motion" : "Motion"}
        </Box>
      )}

      {/* Sound Effect Badge */}
      {(look.background_sound_effect?.status === "COMPLETED" || isPollingSoundEffect) && (
        <Box
          sx={{
            position: "absolute",
            bottom: 50,
            left: 8,
            zIndex: 5,
            padding: "2px 6px",
            borderRadius: "8px",
            fontSize: "9px",
            fontWeight: "bold",
            backgroundColor: isPollingSoundEffect ? "#FFA500" : "#2196F3",
            color: "white",
            display: "flex",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          {isPollingSoundEffect && (
            <CircularProgress size={8} sx={{ color: "white" }} />
          )}
          {isPollingSoundEffect ? "Processing Sound" : "Sound"}
        </Box>
      )}

      {/* Selected Check Mark */}
      {isSelected && (
        <Box sx={{ position: "absolute", top: 8, left: 8, zIndex: 5 }}>
          <CheckMark isVisible={true} />
        </Box>
      )}

      {/* Currently Selected Indicator */}
      {isCurrentlySelected && !isSelected && (
        <Box
          sx={{
            position: "absolute",
            top: 8,
            left: 8,
            zIndex: 5,
            padding: "2px 6px",
            borderRadius: "8px",
            fontSize: "10px",
            fontWeight: "bold",
            backgroundColor: THEME_COLORS.yellow,
            color: "white",
          }}
        >
          Selected
        </Box>
      )}

      {/* Motion Preview Video */}
      {hasMotion && motionPreviewUrl && (
        <video
          ref={videoRef}
          src={motionPreviewUrl}
          loop
          muted
          playsInline
          preload="metadata"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "150px",
            zIndex: 4,
            opacity: isHovered ? 1 : 0,
            transition: "opacity 0.3s ease-in-out",
            objectFit: "cover",
          }}
        />
      )}

      {look.status === "processing" ? (
        // Gray card for processing looks
        <Box
          sx={{
            width: "100%",
            height: "150px",
            backgroundColor: "#e0e0e0",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            color: "#666",
          }}
        >
          <CircularProgress
            size={20}
            sx={{ color: THEME_COLORS.yellow, mb: 1 }}
          />
          <Typography variant="caption" align="center">
            Processing
            <br />
            <small>Auto-refreshing...</small>
          </Typography>
        </Box>
      ) : (
        // Normal image for successful looks
        <img
          src={getLookImage(look)}
          alt={look.name}
          style={{
            width: "100%",
            height: "150px",
            objectFit: "cover",
          }}
        />
      )}

      <Box sx={{ p: 1, textAlign: "center" }}>
        <Typography variant="body2" noWrap>
          {look.name}
        </Typography>
        {look.gender && (
          <Typography variant="caption" color="textSecondary">
            {look.gender}
          </Typography>
        )}
        {/* Sound Effect Play Button */}
        {soundEffectPreviewUrl && (
          <Box
            onClick={handleSoundEffectToggle}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.5,
              mt: 0.5,
              cursor: "pointer",
              color: isPlayingSoundEffect ? "#F44336" : "#2196F3",
              fontSize: "11px",
              fontWeight: 500,
              "&:hover": {
                opacity: 0.8,
              },
            }}
          >
            {isPlayingSoundEffect ? (
              <StopIcon sx={{ fontSize: 14 }} />
            ) : (
              <PlayArrowIcon sx={{ fontSize: 14 }} />
            )}
            {isPlayingSoundEffect ? "Stop Sound" : "Play Sound"}
          </Box>
        )}
      </Box>

      {/* Hidden audio element for sound effect playback */}
      {soundEffectPreviewUrl && (
        <audio ref={audioRef} src={soundEffectPreviewUrl} preload="metadata" />
      )}
    </Box>
  );

  // Wrap with Tooltip for motion looks
  if (hasMotion && getTooltipContent()) {
    return (
      <Tooltip title={getTooltipContent()} placement="top" arrow>
        {cardContent}
      </Tooltip>
    );
  }

  return cardContent;
};

interface ChooseLookModalProps {
  open: boolean;
  onClose: () => void;
  group: AvatarGroup;
  selectedAvatarLabActors: CustomPhotoAvatar[];
  onSelectAvatarLabActor: (avatars: CustomPhotoAvatar) => void;
  // Motion and sound effect handlers
  onOpenAddMotion?: (look: CustomPhotoAvatar) => void;
  onAddSoundEffect?: (look: CustomPhotoAvatar) => void;
  onDeleteLook?: (lookId: string) => void;
  isAddingMotion?: boolean;
  isAddingSoundEffect?: boolean;
}

const ChooseLookModal: React.FC<ChooseLookModalProps> = ({
  open,
  onClose,
  group,
  selectedAvatarLabActors,
  onSelectAvatarLabActor,
  onOpenAddMotion,
  onAddSoundEffect,
  onDeleteLook,
  isAddingMotion = false,
  isAddingSoundEffect = false,
}) => {
  const [selectedLookId, setSelectedLookId] = useState<string | null>(null);
  const [isLooksInfoOpen, setIsLooksInfoOpen] = useState(false);
  const [selectedLookIds, setSelectedLookIds] = useState<string[]>([]);
  const [availableLooks, setAvailableLooks] = useState<CustomPhotoAvatar[]>([]);
  const [pollingIntervals, setPollingIntervals] = useState<
    Map<string, NodeJS.Timeout>
  >(new Map());
  // Track motion avatars that are polling for motion_preview_url
  const [motionPreviewPolling, setMotionPreviewPolling] = useState<Set<string>>(
    new Set()
  );
  // Track avatars that are polling for sound effect completion
  const [soundEffectPolling, setSoundEffectPolling] = useState<Set<string>>(
    new Set()
  );

  // Extract the actual look objects from the group
  useEffect(() => {
    if (group.looks && Array.isArray(group.looks)) {
      const looks = group.looks.filter(
        (look): look is CustomPhotoAvatar =>
          typeof look === "object" && look !== null && "_id" in look
      );
      setAvailableLooks(looks);
    } else {
      setAvailableLooks([]);
    }
  }, [group.looks]);

  useEffect(() => {
    if (open) {
      const currentlySelectedFromGroup = selectedAvatarLabActors
        .filter((look) => look.avatar_group === group._id)
        .map((look) => look._id);
      setSelectedLookIds(currentlySelectedFromGroup);
    }
  }, [open, selectedAvatarLabActors, group._id]);

  // Polling function for a specific look. Handles both V2 (generation_id) and
  // V3 (avatar_look_id) records — V3 looks attached to an existing trained
  // group used to silently skip polling here because they have no generation_id.
  const pollLookStatus = useCallback(async (look: CustomPhotoAvatar) => {
    if (look.status !== "processing") return;

    const isV3 = look.apiVersion === "v3" || !!look.avatar_look_id;
    if (!isV3 && !look.generation_id) return;
    if (isV3 && !look.avatar_look_id) return;

    try {
      const updatedAvatar = isV3
        ? (await syncCustomPhotoAvatarV3Look(look.avatar_look_id!)).avatar
        : (await checkPhotoAvatarStatus(look.generation_id!)).avatar;

      // Update the look in availableLooks
      setAvailableLooks((prev) =>
        prev.map((item) =>
          item._id === look._id ? { ...item, ...updatedAvatar } : item
        )
      );

      // If still processing, continue polling
      if (updatedAvatar.status === "processing") {
        const intervalId = setTimeout(
          () => pollLookStatus({ ...look, ...updatedAvatar }),
          isV3 ? 5000 : 20000
        );
        setPollingIntervals((prev) => new Map(prev.set(look._id, intervalId)));
      } else {
        // Stop polling if status is success or failed
        clearPollingInterval(look._id);
      }
    } catch (error) {
      console.error(`Error polling look ${look._id}:`, error);
      // Optionally retry or mark as failed
      clearPollingInterval(look._id);
    }
  }, []);

  // Clear polling interval
  const clearPollingInterval = (lookId: string) => {
    const intervalId = pollingIntervals.get(lookId);
    if (intervalId) {
      clearTimeout(intervalId);
      setPollingIntervals((prev) => {
        const newMap = new Map(prev);
        newMap.delete(lookId);
        return newMap;
      });
    }
  };

  // Start polling for processing looks when modal opens
  useEffect(() => {
    if (open) {
      availableLooks.forEach((look) => {
        const canPoll =
          look.status === "processing" &&
          (look.generation_id || look.avatar_look_id);
        if (canPoll) {
          // V3 looks need a fast first check (HeyGen prompt looks finish in
          // seconds). Legacy V2 looks keep the original 20s cadence.
          const isV3 = look.apiVersion === "v3" || !!look.avatar_look_id;
          const initialDelay = isV3 ? 5000 : 20000;
          const intervalId = setTimeout(() => pollLookStatus(look), initialDelay);
          setPollingIntervals(
            (prev) => new Map(prev.set(look._id, intervalId))
          );
        }
      });
    }

    // Cleanup function - clear all intervals when modal closes or component unmounts
    return () => {
      pollingIntervals.forEach((intervalId, lookId) => {
        clearTimeout(intervalId);
      });
      setPollingIntervals(new Map());
    };
  }, [open, availableLooks, pollLookStatus]);

  // Start polling for newly added processing looks
  useEffect(() => {
    if (open) {
      availableLooks.forEach((look) => {
        const canPoll =
          look.status === "processing" &&
          (look.generation_id || look.avatar_look_id) &&
          !pollingIntervals.has(look._id);
        if (canPoll) {
          const isV3 = look.apiVersion === "v3" || !!look.avatar_look_id;
          const initialDelay = isV3 ? 5000 : 20000;
          const intervalId = setTimeout(() => pollLookStatus(look), initialDelay);
          setPollingIntervals(
            (prev) => new Map(prev.set(look._id, intervalId))
          );
        }
      });
    }
  }, [availableLooks, open, pollLookStatus, pollingIntervals]);

  // Refs to store interval IDs so they persist across renders
  const motionPollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const soundEffectPollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Detect motion looks that need polling and add them to the set
  useEffect(() => {
    if (!open) return;

    // Find motion looks without motion_preview_url that aren't already being polled
    const motionLooksNeedingPreview = availableLooks.filter(
      (look) =>
        look.is_motion &&
        !look.motion_preview_url &&
        look.status === "success"
    );

    // Add new looks to the polling set
    motionLooksNeedingPreview.forEach((look) => {
      setMotionPreviewPolling((prev) => {
        if (prev.has(look._id)) return prev;
        const newSet = new Set(prev);
        newSet.add(look._id);
        return newSet;
      });
    });
  }, [open, availableLooks]);

  // Actually poll for motion preview URLs
  useEffect(() => {
    if (!open || motionPreviewPolling.size === 0) {
      // Clear interval if no longer needed
      if (motionPollingIntervalRef.current) {
        clearInterval(motionPollingIntervalRef.current);
        motionPollingIntervalRef.current = null;
      }
      return;
    }

    const pollMotionPreview = async () => {
      // Get current polling IDs from the set
      const pollingIds = Array.from(motionPreviewPolling);

      for (const lookId of pollingIds) {
        try {
          const response = await syncMotionPreviewUrl(lookId);

          if (response.motionPreviewUrl) {
            // Update the look with full avatar data from sync response
            setAvailableLooks((prev) =>
              prev.map((item) =>
                item._id === lookId
                  ? {
                      ...item,
                      motion_preview_url: response.motionPreviewUrl ?? undefined,
                      background_sound_effect: response.avatar.background_sound_effect,
                    }
                  : item
              )
            );
            // Remove from polling set
            setMotionPreviewPolling((prev) => {
              const newSet = new Set(prev);
              newSet.delete(lookId);
              return newSet;
            });
          }
        } catch (error) {
          console.error(`Error syncing motion preview for ${lookId}:`, error);
        }
      }
    };

    // Initial poll
    pollMotionPreview();

    // Set up interval for polling every 30 seconds
    if (!motionPollingIntervalRef.current) {
      motionPollingIntervalRef.current = setInterval(pollMotionPreview, 30000);
    }

    return () => {
      if (motionPollingIntervalRef.current) {
        clearInterval(motionPollingIntervalRef.current);
        motionPollingIntervalRef.current = null;
      }
    };
  }, [open, motionPreviewPolling]);

  // Detect sound effect looks that need polling and add them to the set
  useEffect(() => {
    if (!open) return;

    // Find looks with processing sound effects that aren't already being polled
    const looksNeedingSoundEffect = availableLooks.filter(
      (look) =>
        look.background_sound_effect &&
        (look.background_sound_effect.status === "PROCESSING" ||
          look.background_sound_effect.status === "PENDING")
    );

    // Add new looks to the polling set
    looksNeedingSoundEffect.forEach((look) => {
      setSoundEffectPolling((prev) => {
        if (prev.has(look._id)) return prev;
        const newSet = new Set(prev);
        newSet.add(look._id);
        return newSet;
      });
    });
  }, [open, availableLooks]);

  // Actually poll for sound effect completion
  useEffect(() => {
    if (!open || soundEffectPolling.size === 0) {
      // Clear interval if no longer needed
      if (soundEffectPollingIntervalRef.current) {
        clearInterval(soundEffectPollingIntervalRef.current);
        soundEffectPollingIntervalRef.current = null;
      }
      return;
    }

    const pollSoundEffect = async () => {
      // Get current polling IDs from the set
      const pollingIds = Array.from(soundEffectPolling);

      for (const lookId of pollingIds) {
        try {
          const response = await syncMotionPreviewUrl(lookId);
          const soundEffect = response.avatar.background_sound_effect;

          // Check if sound effect is now completed AND has the preview URL
          if (
            soundEffect?.status === "COMPLETED" &&
            (soundEffect?.sound_effect_preview_url ||
              soundEffect?.file_meta?.meta?.audios?.mp3)
          ) {
            // Update the look with the full avatar data from sync response
            setAvailableLooks((prev) =>
              prev.map((item) =>
                item._id === lookId
                  ? {
                      ...item,
                      background_sound_effect: soundEffect,
                    }
                  : item
              )
            );
            // Remove from polling set
            setSoundEffectPolling((prev) => {
              const newSet = new Set(prev);
              newSet.delete(lookId);
              return newSet;
            });
          }
        } catch (error) {
          console.error(`Error syncing sound effect for ${lookId}:`, error);
        }
      }
    };

    // Initial poll
    pollSoundEffect();

    // Set up interval for polling every 30 seconds
    if (!soundEffectPollingIntervalRef.current) {
      soundEffectPollingIntervalRef.current = setInterval(pollSoundEffect, 30000);
    }

    return () => {
      if (soundEffectPollingIntervalRef.current) {
        clearInterval(soundEffectPollingIntervalRef.current);
        soundEffectPollingIntervalRef.current = null;
      }
    };
  }, [open, soundEffectPolling]);

  // Cleanup polling when modal closes
  useEffect(() => {
    if (!open) {
      setMotionPreviewPolling(new Set());
      setSoundEffectPolling(new Set());
      if (motionPollingIntervalRef.current) {
        clearInterval(motionPollingIntervalRef.current);
        motionPollingIntervalRef.current = null;
      }
      if (soundEffectPollingIntervalRef.current) {
        clearInterval(soundEffectPollingIntervalRef.current);
        soundEffectPollingIntervalRef.current = null;
      }
    }
  }, [open]);

  const handleLookSelect = (look: CustomPhotoAvatar) => {
    if (look.status !== "success") return;

    setSelectedLookIds(
      (prev) =>
        prev.includes(look._id)
          ? prev.filter((id) => id !== look._id) // Remove if already selected
          : [...prev, look._id] // Add if not selected
    );
  };

  const getLookImage = (look: CustomPhotoAvatar) => {
    if (look.status !== "success") return "/placeholder-avatar.png";
    // V2 records carry image_url_list; V3 records carry preview_image_url.
    if (look.image_url_list && look.image_url_list.length > 0) {
      return look.image_url_list[0];
    }
    if (look.preview_image_url) return look.preview_image_url;
    return "/placeholder-avatar.png";
  };

  const getLookStatusColor = (status: string) => {
    return status === "success"
      ? "#4CAF50"
      : status === "processing"
      ? "#FFA500"
      : "#F44336";
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="h6">Choose Look for {group.name}</Typography>
            <Tooltip title="Learn more about looks management" arrow>
              <Box
                onClick={() => setIsLooksInfoOpen(true)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  cursor: "pointer",
                  color: THEME_COLORS.darkYellow,
                  "&:hover": {
                    opacity: 0.8,
                  },
                }}
              >
                <InfoOutlinedIcon sx={{ fontSize: 20 }} />
              </Box>
            </Tooltip>
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
          Manage your avatar looks, add motion effects, and select which version to use in your videos.
          {availableLooks.some((look) => look.status === "processing") &&
            " • Auto-refreshing processing looks every 20 seconds"}
        </Typography>

        <Grid container spacing={2}>
          {availableLooks.map((look) => {
            const isSelected = selectedLookIds.includes(look._id);
            const isCurrentlySelected = selectedAvatarLabActors.some(
              (selected) => selected._id === look._id
            );
            const canSelect = look.status === "success";
            const isPolling = pollingIntervals.has(look._id);

            return (
              <Grid item xs={6} sm={4} md={3} key={look._id}>
                <LookCard
                  look={look}
                  isSelected={isSelected}
                  isCurrentlySelected={isCurrentlySelected}
                  canSelect={canSelect}
                  isPolling={isPolling}
                  isPollingMotionPreview={motionPreviewPolling.has(look._id)}
                  isPollingSoundEffect={soundEffectPolling.has(look._id)}
                  onSelectAvatarLabActor={onSelectAvatarLabActor}
                  onOpenAddMotion={onOpenAddMotion}
                  onAddSoundEffect={onAddSoundEffect}
                  onDeleteLook={onDeleteLook}
                  isAddingMotion={isAddingMotion}
                  isAddingSoundEffect={isAddingSoundEffect}
                  getLookStatusColor={getLookStatusColor}
                  getLookImage={getLookImage}
                />
              </Grid>
            );
          })}
        </Grid>

        {availableLooks.length === 0 && (
          <Box textAlign="center" py={4}>
            <Typography color="textSecondary">
              No looks available for this group yet.
            </Typography>
          </Box>
        )}

        {/* Processing Message */}
        {(motionPreviewPolling.size > 0 || soundEffectPolling.size > 0) && (
          <Box
            sx={{
              mt: 3,
              p: 2,
              backgroundColor: "#FFF8E1",
              borderRadius: "8px",
              border: "1px solid #FFE082",
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <CircularProgress size={18} sx={{ color: "#FFA000" }} />
            <Typography variant="body2" sx={{ color: "#5D4037" }}>
              {motionPreviewPolling.size > 0 && soundEffectPolling.size > 0
                ? "Motion and sound effects are being processed."
                : motionPreviewPolling.size > 0
                ? "Motion is being processed."
                : "Sound effect is being processed."}{" "}
              This may take a few minutes. Feel free to close this window and check back later — processing will continue in the background.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ padding: "20px" }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={onClose}
          variant="contained"
          disabled={!selectedLookIds.length}
        >
          Select Looks
        </Button>
      </DialogActions>

      {/* Looks Info Dialog */}
      <Dialog
        open={isLooksInfoOpen}
        onClose={() => setIsLooksInfoOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <InfoOutlinedIcon sx={{ color: THEME_COLORS.darkYellow }} />
            Managing Avatar Looks
          </Box>
          <IconButton
            onClick={() => setIsLooksInfoOpen(false)}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            This is where you manage all the <strong>Looks</strong> for your avatar group — different versions of the same avatar in various settings, outfits, or scenarios.
          </Typography>

          <Typography variant="body1" sx={{ mb: 2 }}>
            Each look you select here will be added to your <strong>Selected Avatars</strong> list, ready to be used when generating videos.
          </Typography>

          <Typography variant="body1" sx={{ mb: 2 }}>
            You can enhance your looks with:
          </Typography>
          <Box component="ul" sx={{ pl: 2, mb: 2 }}>
            <li>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Motion</strong> — Add realistic movement and animations to bring your avatar to life. Hover over motion-enabled looks to preview the animation.
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                <strong>Sound Effect</strong> — Add ambient audio to create atmosphere and enhance the viewing experience. Sound effects can only be added to looks that already have motion.
              </Typography>
            </li>
          </Box>

          <Typography variant="body1" sx={{ mb: 2 }}>
            Use the <strong>three-dot menu</strong> on each look card to add motion, sound effects, or delete looks you no longer need.
          </Typography>

          <Typography
            variant="body2"
            sx={{ color: THEME_COLORS.gray, fontStyle: "italic" }}
          >
            Tip: You can select multiple looks from the same group to use different versions of your avatar in your video projects.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setIsLooksInfoOpen(false)}
            variant="contained"
          >
            Got it
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default ChooseLookModal;
